import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import {
	Badge,
	Box,
	Button,
	Card,
	CardBody,
	CardHeader,
	Flex,
	SimpleGrid,
	Stack,
	Text,
	useColorModeValue,
} from "@chakra-ui/react";
import moment from "moment";

import FinancialStatusOverviewTable from "../../Components/FinancialStatus/FinancialStatusOverviewTable";
import FinancialStatusWarnings from "../../Components/FinancialStatus/FinancialStatusWarnings";
import { EXPENSE_CATEGORIES, formatAmount } from "../../utils/financialStatus";
import {
	buildFinancialStatusOverview,
	FINALIZATION_STATUSES,
	OVERVIEW_CATEGORY_KEYS,
} from "../../utils/financialStatusOverview";
import SearchBox from "../Properties/SearchBox";

const CATEGORY_SUMMARY_ITEMS = [
	{ key: "rent", labelKey: "financialStatus.rentCollected" },
	...EXPENSE_CATEGORIES.map((category) => ({
		key: category.key,
		labelKey: `types.${category.key}`,
		fallbackLabel: category.label,
	})),
];

export default function FinancialStatus() {
	const { t } = useTranslation();
	const [query, setQuery] = useState("");
	const [date, setDate] = useState([moment().year(), moment().month()]);
	const storeProperties = useSelector((state) => state.user?.user?.properties);
	const properties = useMemo(() => (storeProperties || []).filter((property) => !property.archived), [storeProperties]);

	const overview = useMemo(
		() => buildFinancialStatusOverview({ properties, year: date[0], monthIndex: date[1] }),
		[properties, date],
	);

	const panelBg = useColorModeValue("gray.50", "gray.700");
	const muted = useColorModeValue("gray.600", "gray.300");
	const statusCards = [
		{ key: "empty", label: t("properties.empty"), colorScheme: "blue" },
		{ key: "transferred", label: t("financialStatus.rentReceivedAndTransferred"), colorScheme: "green" },
		{ key: "received", label: t("financialStatus.rentReceivedNotTransferred"), colorScheme: "yellow" },
		{ key: "unpaid", label: t("financialStatus.rentNotReceived"), colorScheme: "red" },
	];
	const categoryCards = OVERVIEW_CATEGORY_KEYS.map((key) => {
		const item = CATEGORY_SUMMARY_ITEMS.find((entry) => entry.key === key);
		return {
			key,
			label: t(item.labelKey, { defaultValue: item.fallbackLabel || key }),
			totals: overview.categoryTotals?.[key] || { eur: 0, ron: 0 },
		};
	});

	return (
		<Card>
			<CardHeader>
				<Stack spacing={0}>
					<Text fontSize="xl" fontWeight="bold">
						{t("financialStatus.financialStatus")}
					</Text>
					<Text fontSize="sm" color={muted}>
						{overview.owners.length} owners · {overview.propertyCount} properties
					</Text>
				</Stack>
			</CardHeader>
			<CardBody>
				<Stack spacing={3}>
					<Flex direction="row" alignItems="center" justifyContent="center" gap={4}>
						<Button size="sm" onClick={() => {
							const newDate = moment(date).subtract(1, "months");
							setDate([newDate.year(), newDate.month()]);
						}}>
							{t("table.previousMonth")}
						</Button>
						<Text fontWeight="bold" fontSize="lg" minW="10rem" textAlign="center">
							{moment(date).format("MMMM YYYY")}
						</Text>
						<Button
							size="sm"
							isDisabled={moment().isSame(moment(date), "year") && moment().isSame(moment(date), "month")}
							onClick={() => {
								const newDate = moment(date).add(1, "months");
								setDate([newDate.year(), newDate.month()]);
							}}
						>
							{t("table.nextMonth")}
						</Button>
					</Flex>
					<SimpleGrid columns={{ base: 2, lg: 4 }} spacing={2}>
						{statusCards.map((status) => (
							<Box key={status.key} bg={panelBg} borderWidth="1px" borderRadius="md" px={2.5} py={1.5}>
								<Flex justifyContent="space-between" alignItems="center" gap={2}>
									<Text fontSize="xs" color={muted} noOfLines={2} lineHeight="short">
										{status.label}
									</Text>
									<Badge colorScheme={status.colorScheme} variant="subtle" fontSize="sm" fontWeight="semibold">
										{overview.statusCounts[status.key]}
									</Badge>
								</Flex>
							</Box>
						))}
					</SimpleGrid>

					<Box>
						<Text fontSize="xs" fontWeight="bold" mb={1.5} textTransform="uppercase" letterSpacing="wide" color={muted}>
							{t("financialStatus.monthlyTotals")}
						</Text>
						<SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={2}>
							{FINALIZATION_STATUSES.map((status) => {
								const totals = overview.finalizedTotals[status] || { eur: 0, ron: 0 };
								return (
									<Box key={status} bg={panelBg} borderWidth="1px" borderRadius="md" px={2.5} py={1.5}>
										<Text fontSize="xs" color={muted} mb={0.5} noOfLines={1}>
											{t(`financialStatus.${status}`, { defaultValue: status })}
										</Text>
										<Text fontSize="xs" fontWeight="semibold" lineHeight="short">
											{formatAmount(totals.ron)} RON
										</Text>
										<Text fontSize="xs" fontWeight="semibold" lineHeight="short">
											{formatAmount(totals.eur)} EUR
										</Text>
									</Box>
								);
							})}
						</SimpleGrid>
					</Box>

					<Box>
						<Text fontSize="xs" fontWeight="bold" mb={1.5} textTransform="uppercase" letterSpacing="wide" color={muted}>
							{t("financialStatus.categoryTotals")}
						</Text>
						<SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={2}>
							{categoryCards.map((category) => (
								<Box key={category.key} bg={panelBg} borderWidth="1px" borderRadius="md" px={2.5} py={1.5}>
									<Text fontSize="xs" color={muted} mb={0.5} noOfLines={1}>
										{category.label}
									</Text>
									<Text fontSize="xs" fontWeight="semibold" lineHeight="short">
										{formatAmount(category.totals.ron)} RON
									</Text>
									<Text fontSize="xs" fontWeight="semibold" lineHeight="short">
										{formatAmount(category.totals.eur)} EUR
									</Text>
								</Box>
							))}
						</SimpleGrid>
					</Box>

					<FinancialStatusWarnings warnings={overview.warnings} />
					<Box maxW="20rem" w="100%">
						<SearchBox handleSearch={setQuery} searchOptions={[{ label: "Name", description: "Search by owner name" }]} />
					</Box>
					<FinancialStatusOverviewTable owners={overview.owners} query={query} />
				</Stack>
			</CardBody>
		</Card>
	);
}
